/*
  Dynamic stock status — PDP shipping message + Add-to-Cart label.
  Paired with snippets/dynamic-stock.liquid (emits #dyn-stock-data) + dynamic-stock.css.
  Only present on products carrying the `track-stock` tag.

  Per the currently-selected variant (custom.in_stock / custom.shipping_days):
    • in_stock === true  → green dot + "In stock — arrives on <date>"  + ATC "Add to Cart"
    • else               → amber dot + "Pre-order — arrives on <date>"  + ATC "Pre-ordered - Add to Cart"
  <date> = the shipping_days-th business day counting from today (today = day 1, Sat/Sun
  skipped), formatted "Mon, Jul 20".

  Runs on both surfaces:
    • GemPages — recolors span.pulsating-dot, rewrites the gp-estimate-date text + gp ATC label.
    • Native   — injects a message above button.m-add-to-cart, rewrites its label.

  Page-level orchestrator (not a web component) because it coordinates elements that live
  across GemPages/native markup it does not own, and must survive their re-renders.
*/
(function () {
  if (window.__dynStockBound) return;
  window.__dynStockBound = true;

  const dataEl = document.getElementById('dyn-stock-data');
  if (!dataEl) return;

  let DATA;
  try {
    DATA = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }
  const VARIANTS = DATA.variants || {};
  const CFG = DATA.config || {};

  let expected = null; // { inStock, text, atcLabel } currently applied
  let lastVid = null;
  let observer = null;
  let tickScheduled = false;

  /* ---------- date math ---------- */

  // The Nth business day counting from today (today counts as day 1). Sat/Sun skipped.
  // No extra "+1": shipping_days = 1 → today, = 2 → next business day, etc.
  function addBusinessDays(days) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    if (days <= 0) return d;
    let counted = 0;
    while (true) {
      const wd = d.getDay();
      if (wd !== 0 && wd !== 6) {
        counted += 1;
        if (counted >= days) return d;
      }
      d.setDate(d.getDate() + 1);
    }
  }

  function formatDate(d) {
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  /* ---------- variant resolution ---------- */

  function getCurrentVariantId() {
    const atc = document.querySelector('button.gp-button-atc') || document.querySelector('button.m-add-to-cart');
    const form = atc && atc.closest('form');
    const scoped = form && form.querySelector('input[name="id"]');
    if (scoped && scoped.value) return String(scoped.value);

    const anyId = document.querySelector('form[action*="/cart/add"] input[name="id"], input[name="id"]');
    if (anyId && anyId.value) return String(anyId.value);

    const v = new URL(window.location.href).searchParams.get('variant');
    if (v) return String(v);

    return DATA.initialVariantId != null ? String(DATA.initialVariantId) : null;
  }

  /* ---------- state computation ---------- */

  function stateFor(variantId) {
    const v = VARIANTS[variantId];
    if (!v) return null;
    const customDays = parseInt(v.custom_shipping_date, 10);
    const hasCustom = !isNaN(customDays) && customDays > 0;
    // custom_shipping_date overrides in_stock and forces pre-order behaviour.
    const inStock = hasCustom ? false : v.in_stock === true;
    let dateStr = null;
    const fallbackDays = parseInt(v.shipping_days, 10);
    const days = hasCustom ? customDays : fallbackDays;
    if (!isNaN(days) && days > 0) dateStr = formatDate(addBusinessDays(days + 1));

    let text;
    if (inStock) text = dateStr ? CFG.inStockPrefix + ' ' + dateStr : CFG.inStockNoDate;
    else text = dateStr ? CFG.preorderPrefix + ' ' + dateStr : CFG.preorderNoDate;

    return { inStock, text, atcLabel: inStock ? CFG.addToCart : CFG.preorderButton };
  }

  /* ---------- painters ---------- */

  function paintDot(dot, inStock) {
    dot.classList.remove('dyn-dot--in', 'dyn-dot--pre');
    dot.classList.add(inStock ? 'dyn-dot--in' : 'dyn-dot--pre');
  }

  // GemPages surface. Returns true if the pulsating dot was found and handled.
  function paintGemMessage(state) {
    const dot = document.querySelector('.pulsating-dot');
    if (!dot) return false;
    paintDot(dot, state.inStock);

    // Replace the gp-estimate-date text with ours. Target the inner container (plain
    // div) rather than the <gp-estimate-date> custom element itself.
    const target = document.querySelector('.estimate-date-container') || document.querySelector('gp-estimate-date');
    if (target) {
      let span = target.querySelector('.dyn-stock-text');
      if (!span) {
        target.textContent = '';
        span = document.createElement('span');
        span.className = 'dyn-stock-text';
        target.appendChild(span);
      }
      if (span.textContent !== state.text) span.textContent = state.text;
    }
    return true;
  }

  // Native surface — inject our own message above the ATC button.
  function paintNativeMessage(state) {
    const atc = document.querySelector('button.m-add-to-cart');
    if (!atc) return false;

    let msg = document.querySelector('.dyn-stock-msg[data-dyn-native]');
    if (!msg) {
      msg = document.createElement('div');
      msg.className = 'dyn-stock-msg';
      msg.setAttribute('data-dyn-native', '');
      msg.innerHTML =
        '<span class="dyn-stock-msg__dot"></span><span class="dyn-stock-msg__text"></span>';
      const anchor = atc.closest('.main-product__block-buy_buttons') || atc;
      anchor.parentNode.insertBefore(msg, anchor);
    }
    paintDot(msg.querySelector('.dyn-stock-msg__dot'), state.inStock);
    const txt = msg.querySelector('.dyn-stock-msg__text');
    if (txt.textContent !== state.text) txt.textContent = state.text;
    return true;
  }

  function paintAtcLabel(state) {
    document.querySelectorAll('button.gp-button-atc span.gp-content-product-button').forEach((span) => {
      if (span.textContent.trim() !== state.atcLabel) span.textContent = state.atcLabel;
    });
    document.querySelectorAll('button.m-add-to-cart .m-add-to-cart--text').forEach((span) => {
      if (span.textContent.trim() !== state.atcLabel) span.textContent = state.atcLabel;
    });
    document.querySelectorAll('button.m-add-to-cart[data-atc-text]').forEach((btn) => {
      if (btn.getAttribute('data-atc-text') !== state.atcLabel) btn.setAttribute('data-atc-text', state.atcLabel);
    });
  }

  /* ---------- apply + drift detection ---------- */

  function apply() {
    const vid = getCurrentVariantId();
    const state = vid ? stateFor(vid) : null;
    if (!state) return;
    expected = state;

    if (observer) observer.disconnect();
    const painted = paintGemMessage(state);
    if (!painted) paintNativeMessage(state);
    paintAtcLabel(state);
    if (observer) observer.observe(document.body, { childList: true, subtree: true });
  }

  // GemPages re-renders can revert our label/text — detect and re-apply.
  function hasDrift() {
    if (!expected) return false;
    const gpLabel = document.querySelector('button.gp-button-atc span.gp-content-product-button');
    if (gpLabel && gpLabel.textContent.trim() !== expected.atcLabel) return true;
    const natLabel = document.querySelector('button.m-add-to-cart .m-add-to-cart--text');
    if (natLabel && natLabel.textContent.trim() !== expected.atcLabel) return true;

    const dot = document.querySelector('.pulsating-dot');
    if (dot) {
      // GemPages surface: dot must carry our colour class and our text must be intact.
      if (!dot.classList.contains(expected.inStock ? 'dyn-dot--in' : 'dyn-dot--pre')) return true;
      const gtxt = document.querySelector('.dyn-stock-text');
      if (!gtxt || gtxt.textContent.trim() !== expected.text) return true;
    }
    return false;
  }

  /* ---------- scheduling ---------- */

  function tick() {
    tickScheduled = false;
    const vid = getCurrentVariantId();
    if (vid !== lastVid) {
      lastVid = vid;
      apply();
      return;
    }
    if (hasDrift()) apply();
  }

  function scheduleTick() {
    if (tickScheduled) return;
    tickScheduled = true;
    setTimeout(tick, 120);
  }

  /* ---------- wiring ---------- */

  function init() {
    // Variant changes: size/colour controls on both surfaces bubble `change` to document.
    document.addEventListener('change', scheduleTick, true);
    // URL-driven variant changes (back/forward, gp updates).
    window.addEventListener('popstate', scheduleTick);

    // Safety net for GemPages label/text re-renders.
    observer = new MutationObserver(function () {
      if (hasDrift() && expected) {
        // GemPages re-rendered and overwrote our state (common during variant
        // switches). Re-assert immediately so the old template text never flashes.
        // Disconnect first so our own writes don't re-trigger the observer.
        observer.disconnect();
        paintGemMessage(expected) || paintNativeMessage(expected);
        paintAtcLabel(expected);
        observer.observe(document.body, { childList: true, subtree: true });
      }
      if (!tickScheduled) scheduleTick();
    });

    // Initial paint (also starts the observer) + retries for late GemPages hydration.
    apply();
    lastVid = getCurrentVariantId();
    [300, 800, 1500, 2500].forEach((t) => setTimeout(scheduleTick, t));

    // Ensure the observer runs even if the first apply() bailed (no state yet).
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
